import React, { useEffect, useRef } from "react";
import { classAwardThresholdDescription } from "./bullseyeResultGrouping";

export const GROUPING_SCOPES = [
  { id: "standard", key: "standardGroups", label: "Standard" },
  { id: "twentyTwo", key: "twentyTwoGroups", label: ".22 Only" },
];

export const GROUPING_ELIGIBILITY = {
  match_winner: "Exclude match winner from class awards",
  open_places: "Exclude all three place winners from class awards",
  none: "Allow place winners to receive class awards",
};

export function sameDisplayedGroups(left, right) {
  const policy = groups => (groups || []).map(({ title, classes, classAwardsEligible }) => ({ title, classes, classAwardsEligible: classAwardsEligible !== false }));
  return JSON.stringify(policy(left)) === JSON.stringify(policy(right));
}

export function GroupingTabs({ id, label, items, value, onChange }) {
  const buttons = useRef([]);
  function onKeyDown(event, index) {
    let next;
    if (event.key === "ArrowRight") next = (index + 1) % items.length;
    else if (event.key === "ArrowLeft") next = (index + items.length - 1) % items.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = items.length - 1;
    else return;
    event.preventDefault();
    onChange(items[next].id);
    buttons.current[next]?.focus();
  }
  return <div className="grouping-tabs" role="tablist" aria-label={label}>
    {items.map((item, index) => <button key={item.id} ref={node => { buttons.current[index] = node; }}
      type="button" role="tab" id={`${id}-${item.id}`} aria-controls={`${id}-panel`}
      aria-label={item.count !== undefined ? `${item.label} ${item.count}` : item.label}
      aria-selected={value === item.id} tabIndex={value === item.id ? 0 : -1}
      onKeyDown={event => onKeyDown(event, index)} onClick={() => onChange(item.id)}>
      {item.label}{item.count !== undefined && <span>{item.count}</span>}
    </button>)}
  </div>;
}

export function GroupingDialog({ id, title, open, onClose, busy = false, children, actions }) {
  const ref = useRef(null);
  const titleRef = useRef(null);
  const bodyRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const node = ref.current;
    const trigger = document.activeElement;
    node?.showModal();
    titleRef.current?.focus({ preventScroll: true });
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    return () => {
      node?.close();
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [open]);
  return <dialog ref={ref} className="grouping-dialog" aria-labelledby={`${id}-title`}
    onCancel={event => { event.preventDefault(); if (!busy) onClose(); }}>
    <div className="grouping-dialog-layout">
      <header><h2 ref={titleRef} tabIndex={-1} id={`${id}-title`}>{title}</h2></header>
      <div ref={bodyRef} className="grouping-dialog-body">{children}</div>
      <footer className="grouping-actions">{actions}</footer>
    </div>
  </dialog>;
}

const groupDescription = groups => groups.map(group => group.title).join("; ");
const excludedWinnerDisplay = settings => settings?.classAwardExclusion === "none" ? "No excluded winners"
  : settings?.showExcludedClassWinners === true ? "Shown with exclusion labels" : "Hidden from class tables";

export function GroupingPolicySummary({ settings, previous, counts, flatTwentyTwo = false }) {
  return <div className="grouping-policy-summary">
    <div className="grouping-review-scopes">{GROUPING_SCOPES.map(scope => {
      if (flatTwentyTwo && scope.id === "twentyTwo") return <section key={scope.id}>
        <h3>{scope.label}</h3><p className="grouping-caption">All classifications together. No class awards.</p>
        {counts && <p>{Object.values(counts.twentyTwo).reduce((sum, count) => sum + count, 0)} entrants</p>}
      </section>;
      const changed = previous && !sameDisplayedGroups(previous[scope.key], settings[scope.key]);
      return <section key={scope.id}>
        <h3>{scope.label}{changed && <small>Changed</small>}</h3>
        <ul>{settings[scope.key].map(group => <li key={group.id}>
          <span><strong>{group.title}</strong>{group.classes.join(" / ") !== group.title && <small>{group.classes.join(" + ")}</small>}{group.classAwardsEligible === false && <small>Open awards only</small>}</span>
          {counts && <span>{group.classes.reduce((total, cls) => total + (counts[scope.id][cls] || 0), 0)} entrants</span>}
        </li>)}</ul>
        {changed && <p className="grouping-caption">Previously: {groupDescription(previous[scope.key])}</p>}
      </section>;
    })}</div>
    <dl className="grouping-policy-values">
      <div><dt>Class-award eligibility</dt><dd>{GROUPING_ELIGIBILITY[settings.classAwardExclusion]}
        {previous && previous.classAwardExclusion !== settings.classAwardExclusion && <small>Previously: {GROUPING_ELIGIBILITY[previous.classAwardExclusion]}</small>}
      </dd></div>
      <div><dt>Excluded winners: web and PDF</dt><dd>{excludedWinnerDisplay(settings)}
        {previous && excludedWinnerDisplay(previous) !== excludedWinnerDisplay(settings)
          && <small>Previously: {excludedWinnerDisplay(previous)}</small>}
      </dd></div>
      <div><dt>Minimum entrants per class</dt><dd>{settings.minimumEntries ? `${settings.minimumEntries} (grouping criterion)` : "Not set"}
        {previous && previous.minimumEntries !== settings.minimumEntries && <small>Previously: {previous.minimumEntries || "Not set"}</small>}
      </dd></div>
      <div><dt>Class award counts</dt><dd>{classAwardThresholdDescription(settings.classAwardThresholds)}
        {previous && JSON.stringify(previous.classAwardThresholds ?? null) !== JSON.stringify(settings.classAwardThresholds ?? null)
          && <small>Previously: {classAwardThresholdDescription(previous.classAwardThresholds)}</small>}
      </dd></div>
      <div><dt>Program notes</dt><dd>{settings.programNotes || "None"}
        {previous && previous.programNotes !== settings.programNotes && <small>Changed from the published version</small>}
      </dd></div>
      <div><dt>Tie-break firing order</dt><dd>{settings.tieBreakFiringOrder?.length ? `${settings.tieBreakFiringOrder.length} individual matches confirmed` : "Not confirmed"}
        {previous && JSON.stringify(previous.tieBreakFiringOrder || []) !== JSON.stringify(settings.tieBreakFiringOrder || []) && <small>Changed from the published version</small>}
      </dd></div>
    </dl>
  </div>;
}
